import { LogOut, Settings, X } from "lucide-react";

import type { SmileSimulationController } from "../hooks/useSmileSimulationController";
import {
  SMILE_SIMULATION_LANGUAGE_LABELS,
  getSmileSimulationCopy,
} from "../model/i18n";

type SmileSimulationSettingsPanelProps = {
  isOpen: boolean;
  isExperience?: boolean;
  settings: SmileSimulationController["settings"];
  onClose: () => void;
  onOpen: () => void;
  onLogout: SmileSimulationController["actions"]["logout"];
  onUpdateLanguage: SmileSimulationController["actions"]["updateSimulationLanguage"];
  onUpdatePromptVersion: SmileSimulationController["actions"]["updateSimulationPromptVersion"];
};

export function SmileSimulationSettingsPanel({
  isOpen,
  isExperience = false,
  settings,
  onClose,
  onOpen,
  onLogout,
  onUpdateLanguage,
  onUpdatePromptVersion,
}: SmileSimulationSettingsPanelProps) {
  const copy = getSmileSimulationCopy(isExperience ? "ko" : settings.language);

  return (
    <>
      <button
        type="button"
        onClick={onOpen}
        aria-label="시뮬레이션 설정 열기"
        className="absolute right-4 z-30 flex h-11 w-11 items-center justify-center rounded-full border border-white/70 bg-white/90 text-[#8F14E5] shadow-[0_14px_30px_rgba(17,17,17,0.12)] backdrop-blur-sm transition hover:bg-white"
        style={{ top: "max(1rem, env(safe-area-inset-top))" }}
      >
        <Settings className="h-5 w-5" />
      </button>

      {isOpen ? (
        <div className="absolute inset-0 z-40">
          <button
            type="button"
            aria-label="시뮬레이션 설정 닫기"
            onClick={onClose}
            className="absolute inset-0 bg-[#1f1230]/20 backdrop-blur-[2px]"
          />
          <aside
            className="absolute right-0 top-0 flex h-full w-[min(22rem,calc(100vw-2rem))] flex-col gap-5 rounded-l-[2rem] border-l border-white/70 bg-white/95 p-5 shadow-[-20px_0_50px_rgba(17,17,17,0.16)]"
            style={{
              paddingTop: "max(1.25rem, env(safe-area-inset-top))",
              paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))",
            }}
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#8F14E5]/70">
                  Settings
                </p>
                <h2 className="mt-1 text-xl font-semibold text-[#1f1230]">
                  {copy.settings.title}
                </h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="닫기"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-[#f6efff] text-[#5f3a78] transition hover:bg-[#efddff]"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid gap-4">
              {!isExperience ? (
                <label className="space-y-2">
                  <span className="text-sm font-semibold text-[#1f1230]">
                    {copy.settings.language}
                  </span>
                  <select
                    value={settings.language}
                    onChange={(event) =>
                      onUpdateLanguage(
                        event.target.value as Parameters<
                          typeof onUpdateLanguage
                        >[0],
                      )
                    }
                    className="w-full rounded-2xl border border-[#eadcf7] bg-white px-4 py-3 text-sm font-semibold text-[#1f1230] outline-none transition focus:border-[#8F14E5] focus:ring-4 focus:ring-[#8F14E5]/10"
                  >
                    {Object.entries(SMILE_SIMULATION_LANGUAGE_LABELS).map(
                      ([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ),
                    )}
                  </select>
                </label>
              ) : null}

              <label className="space-y-2">
                <span className="text-sm font-semibold text-[#1f1230]">
                  {copy.settings.prompt}
                </span>
                <select
                  value={settings.promptVersion}
                  onChange={(event) =>
                    onUpdatePromptVersion(
                      event.target.value as Parameters<
                        typeof onUpdatePromptVersion
                      >[0],
                    )
                  }
                  className="w-full rounded-2xl border border-[#eadcf7] bg-white px-4 py-3 text-sm font-semibold text-[#1f1230] outline-none transition focus:border-[#8F14E5] focus:ring-4 focus:ring-[#8F14E5]/10"
                >
                  <option value="aesthetic-freeform">
                    Aesthetic-Freeform (심미성-자유형)
                  </option>
                  <option value="vertical-lock">
                    Vertical-Lock (세로축-고정형)
                  </option>
                </select>
              </label>

              {!isExperience ? (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    void onLogout();
                  }}
                  className="flex items-center justify-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-600 transition hover:bg-red-100"
                >
                  <LogOut className="h-5 w-5" />
                  {copy.settings.logout}
                </button>
              ) : null}
            </div>
          </aside>
        </div>
      ) : null}
    </>
  );
}
